import { useNavigate } from 'react-router-dom';
import { Typography, Button, Space, Card, Tag, Table, App, Tooltip } from 'antd';
import { SyncOutlined, FolderOpenOutlined, BgColorsOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IssuerKey, IssuerSummary } from '@mygate/common';
import { issuersService } from '@/services/issuers';

const { Text } = Typography;

function formatDate(value: Date | string | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

/** Registered RWA content feeds, their mirror health, and the door into each catalog. */
export function IssuersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const { data, isLoading } = useQuery({
    queryKey: ['issuers'],
    queryFn: async () => (await issuersService.listIssuers()).data ?? [],
    staleTime: 1000 * 30,
  });

  const syncMutation = useMutation({
    mutationFn: (issuer: IssuerKey) => issuersService.triggerSync(issuer),
    onSuccess: response => {
      if (response.data?.skipped) message.info(response.data.message);
      else message.success(`${response.data?.message ?? 'Sync enqueued'} — refresh in a minute`);
      queryClient.invalidateQueries({ queryKey: ['issuers'] });
    },
    onError: error => message.error(error instanceof Error ? error.message : 'Failed to enqueue sync'),
  });

  const columns: ColumnsType<IssuerSummary> = [
    {
      title: 'Issuer',
      key: 'issuer',
      width: 200,
      render: (_, issuer) => (
        <Space direction="vertical" size={0}>
          <Text strong>{issuer.displayName}</Text>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
            {issuer.baseUrl}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Assets',
      key: 'counts',
      width: 170,
      render: (_, issuer) => (
        <Space wrap>
          <Tag>{issuer.counts.active} active</Tag>
          <Tag color="green">{issuer.counts.linked} linked</Tag>
          {issuer.counts.unlinked > 0 ? (
            <Tag color="orange">{issuer.counts.unlinked} unlinked</Tag>
          ) : (
            <Tag color="default">0 unlinked</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Last sync',
      key: 'lastSync',
      render: (_, issuer) => {
        const sync = issuer.lastSync;
        if (!sync) return <Text type="secondary">never</Text>;
        return (
          <Space direction="vertical" size={0}>
            <Space wrap>
              <Text>{formatDate(sync.lastRunAt)}</Text>
              {sync.error ? (
                <Tooltip title={sync.error}>
                  <Tag color="red">failed</Tag>
                </Tooltip>
              ) : sync.complete ? (
                <Tag color="green">complete</Tag>
              ) : (
                <Tag color="orange">incomplete</Tag>
              )}
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              seen {sync.assetsSeen} · upserted {sync.assetsUpserted} · deactivated {sync.assetsDeactivated} ·
              auto-linked {sync.autoLinked} · {sync.lastRunDurationMs} ms
            </Text>
            {sync.activeAssetsBlocked > 0 && (
              <Tooltip title="Live RWA assets whose activation checklist no longer passes — usually the issuer stopped publishing content they are linked to. They stay live until someone decides otherwise.">
                <Tag color="red">{sync.activeAssetsBlocked} live asset(s) need attention</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, issuer) => (
        <Space wrap>
          <Button
            icon={<SyncOutlined />}
            onClick={() => syncMutation.mutate(issuer.issuer)}
            loading={syncMutation.isPending && syncMutation.variables === issuer.issuer}
          >
            Sync now
          </Button>
          <Button type="primary" icon={<FolderOpenOutlined />} onClick={() => navigate(`/issuers/${issuer.issuer}`)}>
            Open catalog
          </Button>
          <Button icon={<BgColorsOutlined />} onClick={() => navigate(`/issuers/${issuer.issuer}/brand`)}>
            Brand space
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Externe Emittentenkataloge und Synchronisierung im Blick.</Typography.Text>
      </div>

      <Card>
        <Table
          tableLayout="fixed"
          scroll={{ x: 850 }}
          columns={columns}
          dataSource={data ?? []}
          rowKey="issuer"
          loading={isLoading}
          pagination={false}
        />
      </Card>
    </>
  );
}
